/// <amd-module name="TFS/Admin/Views/ProjectOverview/ProjectOverview" />
import React = require("react");

import AdminResources = require("TFS/Admin/Views/ProjectOverview/Resources");

import { Overlay } from "OfficeFabric/Overlay";
import { ProjectState, ProjectVisibility, TeamProject } from "Tfs/Platform/Contracts/Core";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { Uri } from "VSS/Platform/Util/Url";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField, TextFieldWithMessage } from "VSSUI/TextField";
import { VssPersona } from "VSSUI/VssPersona";

import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IOperationsRestClient } from "TFS/Admin/Common/RestClient/Operations";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { wait } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { OperationStatus } from "VSS/Platform/Contracts/Operations";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, RegionComponent, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { traceException } from "VSS/Platform/Trace";
import { Button } from "VSSUI/Button";

import { ImageData, InputFileSelect } from "TFS/Admin/Common/Contracts/AvatarImagePicker.types";
import { IDeleteProjectDialogProps } from "TFS/Admin/Common/Controls/DeleteProjectDialog/DeleteProjectDialog.Props";
import { IRenameDialogProps } from "TFS/Admin/Common/Controls/RenameDialog/RenameDialog.Props";
import { validateProjectName } from "TFS/Admin/Common/Util/NameValidator";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageService } from "VSS/Platform/Page";
import { Observer } from "VSSUI/Observer";
import { IPickListSelection, PickListDropdown } from "VSSUI/PickList";
import { KeyCode } from "VSSUI/Util";
import { VssIconType } from "VSSUI/VssIcon";
import { ProjectAdminsComponent as ProjectAdmins } from "./ProjectAdmins";
import { ServicePickerComponent as ServicePicker } from "./ServicePicker";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ResourceUsageComponent } from "TFS/Admin/Common/Components/ResourceUsage";

interface IProjectOverviewData {
    displayName: string;
    processTemplateName: string;
    projectVisibility: ProjectVisibilityType;
    deleteProjectOptions: {
        projectId: string;
        hasDeletePermission: boolean;
    };
    editProjectOptions: {
        description: string;
        name: string; // same as DisplayName
        projectId: string;
        hasGenericWritePermission: boolean;
        hasRenamePermission: boolean;
        hasUpdateVisibilityPermission: boolean;
        showOrgVisibilityOption: boolean;
        showPublicVisibilityOption: boolean;
        isPublicVisibilityOptionEnabled: boolean;
        isOrgVisibilityOptionEnabled: boolean;
    };
}

export interface IProjectOverviewDelayLoadData {
    projectAdmins: {
        totalIdentityCount: number;
        identities: IGraphViewModel[];
        groupDescriptor: string;
        canAddMemberToAdminGroup: boolean;
        collectionName: string;
        orgSettingsUrl: string;
    };
    defaultTeamId: string;
    defaultResetProjectImageUrl: string;
}

interface IGraphViewModel {
    displayName: string;
    mailAddress?: string;
    scope?: string;
    subjectKind: string;
    identityId: string;
    descriptor: string;
}

const dataProviderId = "ms.vss-admin-web.project-admin-overview-data-provider";
const delayLoadDataProviderId = "ms.vss-admin-web.project-admin-overview-delay-load-data-provider";
/** Interval, in ms, between calls to monitor operation progress */
const progressPollInterval = 500;
const updateCheckCount = 1000;

interface IProjectOverviewComponentState {
    data: IProjectOverviewData;
    delayLoadData?: IProjectOverviewDelayLoadData;
    error?: string;
    showDeleteProjectDialog?: boolean;
    showChangeImageDialog?: boolean;
    showRenameDialog?: boolean;
    updateInProgress?: boolean;
    showSaveBtn?: boolean;
}

export interface IProjectOverviewComponentProps {
    handleFile?: (file: File) => void;
}

interface IUpdateProjectNode {
    name?: string;
    description?: string;
    visibility?: ProjectVisibility;
    state?: ProjectState;
}

interface IProjectAvatar {
    image: number[];
}
interface IVisibilityOptions {
    key?: ProjectVisibilityType;
    text?: string;
    disabled?: boolean;
}
export namespace ProjectVisibilityConstants {
    export const EveryoneInTenant = "EveryoneInTenant";
    export const TeamMembers = "TeamMembers";
    export const Everyone = "Everyone";
}
export type ProjectVisibilityType =
    | typeof ProjectVisibilityConstants.EveryoneInTenant
    | typeof ProjectVisibilityConstants.TeamMembers
    | typeof ProjectVisibilityConstants.Everyone;

export function projectVisibilityStringToEnum(projectVisibilityString: ProjectVisibilityType): ProjectVisibility {
    switch (projectVisibilityString) {
        case ProjectVisibilityConstants.EveryoneInTenant:
            return ProjectVisibility.Organization;
        case ProjectVisibilityConstants.Everyone:
            return ProjectVisibility.Public;
        case ProjectVisibilityConstants.TeamMembers:
            return ProjectVisibility.Private;
        default:
            throw new Error(format(AdminResources.VisibilityValueInvalid, projectVisibilityString));
    }
}

export class ProjectOverviewComponent extends VssComponent<{}, IProjectOverviewComponentState> {
    private _operationId: string | null;
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private projectNameChanged: boolean = false;
    private descriptionChanged: boolean = false;
    private visibilityChanged: boolean = false;
    private currentVisibility: ProjectVisibilityType;
    private descriptionTextField: ObservableValue<string>;
    private projectNameTextField: ObservableValue<string>;
    private editImageEl: HTMLElement;
    private projectImageUrl: string = "";
    private hostPath: string = "";
    private imageUpdateInProgress: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private updateNode: IUpdateProjectNode = {};
    private projectNameValidationMessage: ObservableValue<string> = new ObservableValue<string>("");
    private isHosted: boolean;

    public constructor(props: IProjectOverviewComponentProps, context?: any) {
        super(props, context);

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        if (vssPageData) {
            this.isHosted = vssPageData.isHosted;
            this.hostPath = vssPageData.hostPath;
        }

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.state = {
            data: contributionService.getData<IProjectOverviewData>(dataProviderId)!,
            delayLoadData: undefined
        };

        if (this.state.data) {
            this.projectNameTextField = new ObservableValue<string>(this.state.data.editProjectOptions.name);
            this.descriptionTextField = new ObservableValue<string>(this.state.data.editProjectOptions.description);
        }
    }

    public componentDidMount() {
        super.componentDidMount();

        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProjectOverview);

        this._updateProjectImageUrl();

        if (this.state.data) {
            this._updateDelayLoad(this.state.data.editProjectOptions.projectId);
        }
    }

    public render(): JSX.Element {
        const data = this.state.data;
        if (!data) {
            return (
                <div className="project-overview">
                    <MessageBar className="message-bar" severity={MessageBarSeverity.Error}>
                        {AdminResources.DataProviderError}
                    </MessageBar>
                </div>
            );
        }

        const delayLoadData = this.state.delayLoadData;
        const subheading = (heading: string) => (
            <h5 className="fontSizeM" role="none">
                {heading}
            </h5>
        );

        const visibilityOptions: IVisibilityOptions[] = [];
        visibilityOptions.push({
            key: ProjectVisibilityConstants.TeamMembers,
            text: AdminResources.PrivateVisibilityTitle
        });
        if (data.editProjectOptions.showOrgVisibilityOption) {
            visibilityOptions.push({
                key: ProjectVisibilityConstants.EveryoneInTenant,
                text: AdminResources.OrganizationVisibilityTitle,
                disabled: !data.editProjectOptions.isOrgVisibilityOptionEnabled
            });
        }
        if (data.editProjectOptions.showPublicVisibilityOption) {
            visibilityOptions.push({
                key: ProjectVisibilityConstants.Everyone,
                text: AdminResources.PublicVisibilityTitle,
                disabled: !data.editProjectOptions.isPublicVisibilityOptionEnabled
            });
        }

        if (this.currentVisibility == null) {
            this.currentVisibility = data.projectVisibility;
        }
        const selectedOptions: IVisibilityOptions[] = [];
        visibilityOptions.forEach((item: any) => {
            if (item.key === this.currentVisibility) {
                selectedOptions.push(item);
            }
        });

        return (
            <div className="project-overview">
                {this.state.error ? (
                    <MessageBar severity={MessageBarSeverity.Error} onDismiss={() => this.setState({ error: undefined })} className="message-bar">
                        {this.state.error}
                    </MessageBar>
                ) : null}
                <div className="flex-row overview-fields">
                    <div>
                        <h2 className="title-l">{AdminResources.ProjectDetailsHeading}</h2>
                        <div className="project-name-field">
                            {subheading(AdminResources.NameHeading)}
                            <Observer projectNameValidationMessage={this.projectNameValidationMessage}>
                                {(props: { projectNameValidationMessage: string }) => (
                                    <TextFieldWithMessage
                                        message={props.projectNameValidationMessage}
                                        error={Boolean(props.projectNameValidationMessage)}
                                        textFieldProps={{
                                            value: this.projectNameTextField,
                                            disabled: !data.editProjectOptions.hasRenamePermission,
                                            onChange: this._onNameChanged,
                                            onBlur: this._onNameLostFocus,
                                            ariaLabel: AdminResources.NameHeading
                                        }}
                                    />
                                )}
                            </Observer>
                        </div>

                        <div className="description-field">
                            {subheading(AdminResources.DescriptionHeading)}
                            <TextField
                                value={this.descriptionTextField}
                                multiline
                                disabled={!data.editProjectOptions.hasGenericWritePermission}
                                onChange={this._onDescriptionChanged}
                                ariaLabel={AdminResources.DescriptionHeading}
                            />
                        </div>

                        <RegionComponent componentRegion="ProjectOverviewAdminRegion" />

                        {data.editProjectOptions.isPublicVisibilityOptionEnabled || data.editProjectOptions.isOrgVisibilityOptionEnabled ? (
                            <div>
                                {subheading(AdminResources.VisibilityHeading)}
                                <PickListDropdown
                                    className="visibility-field"
                                    selectedItems={selectedOptions}
                                    getListItem={(item: any) => {
                                        return {
                                            key: item.key,
                                            name: item.text,
                                            iconProps: {
                                                iconType: VssIconType.fabric,
                                                iconName: this._getIconClass(item.key)
                                            },
                                            disabled: item.disabled
                                        };
                                    }}
                                    getPickListItems={() => {
                                        return visibilityOptions;
                                    }}
                                    disabled={!data.editProjectOptions.hasUpdateVisibilityPermission}
                                    onSelectionChanged={this._onVisibilityChanged.bind(this)}
                                />
                                <p className="note fontSizeS">
                                    {AdminResources.UpdateVisibilityNote}
                                    &nbsp;
                                    <Link
                                        className="learn-more-link fontSizeS"
                                        href="https://docs.microsoft.com/en-us/azure/devops/organizations/public/make-project-public?view=vsts"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={AdminResources.UpdateVisibilityLearnMoreAriaLabel}
                                    >
                                        {AdminResources.UpdateVisibilityLearnMoreAriaLabel}
                                    </Link>
                                </p>
                            </div>
                        ) : null}

                        <div className="saveBtn">
                            <Button
                                primary={true}
                                disabled={!this.state.showSaveBtn || this.state.updateInProgress}
                                onClick={this._onSaveProjectDetailsClick}
                            >
                                {AdminResources.SaveButton}
                            </Button>
                        </div>
                    </div>
                    {this.state.delayLoadData ? (
                        <div className="userImage">
                            <Observer imageUpdateInProgress={this.imageUpdateInProgress}>
                                {(props: { imageUpdateInProgress: boolean }) =>
                                    !props.imageUpdateInProgress ? (
                                        <div>
                                            {data.editProjectOptions.hasGenericWritePermission ? (
                                                <div
                                                    className="persona-list-element profile-card-tab-element image-container"
                                                    aria-label={AdminResources.ChangeImageClick}
                                                    onClick={() => {
                                                        this._onChangeImageClick();
                                                    }}
                                                    data-is-focusable={true}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={this._onChangeImageKeyDown}
                                                    ref={el => {
                                                        this.editImageEl = el as HTMLElement;
                                                    }}
                                                >
                                                    {this.getProjectImage()}
                                                    <div className="overlay" />
                                                    <Icon iconName="Edit" className="ms-font-l editIcon" />
                                                </div>
                                            ) : (
                                                this.getProjectImage()
                                            )}
                                        </div>
                                    ) : (
                                        <span>
                                            <Spinner size={SpinnerSize.large} className="imageSpinner" />
                                        </span>
                                    )
                                }
                            </Observer>
                        </div>
                    ) : null}
                </div>

                {this.isHosted && delayLoadData && delayLoadData.projectAdmins ? (
                    <React.Fragment>
                        <div className="overview-divider" />
                        <ProjectAdmins
                            orgName={delayLoadData.projectAdmins.collectionName}
                            totalIdentityCount={delayLoadData.projectAdmins.totalIdentityCount}
                            canAddMemberToAdminGroup={delayLoadData.projectAdmins.canAddMemberToAdminGroup}
                            groupDescriptor={delayLoadData.projectAdmins.groupDescriptor}
                            identities={delayLoadData.projectAdmins.identities}
                            orgSettingsUrl={delayLoadData.projectAdmins.orgSettingsUrl}
                        />
                    </React.Fragment>
                ) : null}

                <div className="overview-divider" />

                <ServicePicker />

                {isFeatureFlagEnabled(this.context.pageContext, "ResourceUsage.Service.Project", false) ?
                (<><div className="overview-divider" /><ResourceUsageComponent source='ProjectOverview' projectId={this.state.data.editProjectOptions.projectId} resourceUsageName='Project Usage Limit'/></>) : null }

                {data.deleteProjectOptions.hasDeletePermission ? (
                    <div>
                        <div className="overview-divider" />
                        <div className="delete-project">
                            {subheading(AdminResources.DeleteProjectHeading)}
                            <p className="note">
                                {AdminResources.DeleteProjectNote}
                                <br />
                                <Link
                                    className="learn-more-link"
                                    href={AdminResources.DeleteProjectLearnMoreLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={AdminResources.DeleteProjectLearnMoreAriaLabel}
                                >
                                    {AdminResources.DeleteProjectLearnMoreAriaLabel}
                                </Link>
                            </p>
                            <Button
                                className="delete-project-button"
                                danger={true}
                                disabled={this.state.updateInProgress}
                                onClick={this._onDeleteProjectClick}
                            >
                                {AdminResources.DeleteButton}
                            </Button>
                        </div>
                    </div>
                ) : null}

                {this.state.showRenameDialog ? (
                    <WrappedComponent<IRenameDialogProps>
                        dependencies={["ms.vss-admin-web.admin-common"]}
                        wrappedType={"ms.vss-admin-web.rename-dialog"}
                        wrappedProps={{
                            name: data.displayName,
                            onCancel: () => this.setState({ showRenameDialog: false }),
                            onRename: this._saveProjectDetails,
                            renameDialogTitle: AdminResources.RenameProjectDialogTitle,
                            renameDialog1: AdminResources.RenameProjectDialog1,
                            renameDialog2: AdminResources.RenameProjectDialog2,
                            renameDialog2Link: AdminResources.RenameProjectDialog2Link,
                            renameConfirmationText: AdminResources.RenameProjectConfirmationText,
                            renameNewName: AdminResources.RenameProjectNewName,
                            invalidNameMessage: AdminResources.InvalidProjectNameMessage,
                            newName: this._getNewProjectName(),
                            validateName: validateProjectName
                        }}
                    />
                ) : null}

                {this.state.updateInProgress ? (
                    <Overlay className="flex flex-column flex-center justify-center">
                        <Spinner size={SpinnerSize.large} />
                    </Overlay>
                ) : null}

                {this.state.showDeleteProjectDialog ? (
                    <WrappedComponent<IDeleteProjectDialogProps>
                        dependencies={["ms.vss-admin-web.admin-common"]}
                        wrappedType={"ms.vss-admin-web.delete-project-dialog"}
                        wrappedProps={{
                            onCancel: () => this.setState({ showDeleteProjectDialog: false }),
                            pageContext: this.context.pageContext,
                            onDeleteProject: this._onDeleteProject,
                            projectName: data.displayName
                        }}
                    />
                ) : null}

                {this.state.showChangeImageDialog ? (
                    <WrappedComponent
                        dependencies={["ms.vss-features.ui-avatar-image-picker-content"]}
                        wrappedType={"vss.avatar-image-picker"}
                        wrappedProps={{
                            onCancel: (hasChanges: boolean) => {
                                this._cancelImageUpload(hasChanges);
                            },
                            onSave: (resetImage: boolean, imageData: ImageData) => {
                                this._saveImageAction(resetImage, imageData);
                            },
                            onFileSelected: (error: string, imageData: ImageData) => {
                                return this._onImageFileSelected(error, imageData);
                            },
                            onLoadDisplayAvatarUrl: this.projectImageUrl,
                            title: AdminResources.ChangeImageTitle,
                            showInitialsOnImageError: true,
                            displayName: data.displayName,
                            defaultResetAvatarUrl: this.state.delayLoadData?.defaultResetProjectImageUrl
                        }}
                    />
                ) : null}
            </div>
        );
    }

    private getProjectImage(): JSX.Element {
        return this.projectImageUrl !== "" ? (
            <VssPersona
                imageUrl={this.projectImageUrl}
                displayName={this.state.data.displayName}
                suppressPersonaCard={true}
                imgAltText={AdminResources.ProjectImageAlt}
                size="extra-extra-large"
                cssClass="project-persona"
            />
        ) : (
            <VssPersona displayName={this.state.data.displayName} suppressPersonaCard={true} size="extra-extra-large" cssClass="project-persona" />
        );
    }

    private _getNewProjectName(): string {
        if (this.projectNameTextField) {
            return this.projectNameTextField.value;
        }
        return "";
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.ProjectOverview, Feature.ProjectOverview, action, properties);
    };

    private _onChangeImageClick = (): void => {
        this.setState({ showChangeImageDialog: true });
        this.editImageEl.blur();
    };

    private _onChangeImageKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) {
            this._onChangeImageClick();
        }
    };

    private _updateProjectImageUrl() {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = tfsPageService.getData();
        const imageURL = pageData?.project?.imageUrl;

        this.projectImageUrl = imageURL ?? "";

        this.imageUpdateInProgress.value = false;
    }

    private _getIconClass(visibility: string): string {
        switch (visibility) {
            case ProjectVisibilityConstants.TeamMembers:
                return "Lock";
            case ProjectVisibilityConstants.EveryoneInTenant:
                return "CityNext";
            case ProjectVisibilityConstants.Everyone:
                return "Globe";
            default:
                throw new Error(format(AdminResources.VisibilityValueInvalid, visibility));
        }
    }

    private _onNameChanged = (event: any, value: string) => {
        this.projectNameTextField.value = value;
        this.projectNameValidationMessage.value = !validateProjectName(value) ? format(AdminResources.InvalidProjectNameMessage, value) : "";

        this.projectNameChanged = value !== (this.state.data.displayName || "") && value !== "";
        this._showSaveBtn(this.projectNameChanged || this.descriptionChanged || this.visibilityChanged);
    };

    private _onNameLostFocus = (): void => {
        // Reset the display name if the value was cleared
        this.projectNameTextField.value = this.projectNameTextField.value || this.state.data.displayName;
    };

    private _onDescriptionChanged = (event: any, value: string) => {
        this.descriptionTextField.value = value;

        this.descriptionChanged = value !== (this.state.data.editProjectOptions.description || "");
        this._showSaveBtn(this.projectNameChanged || this.descriptionChanged || this.visibilityChanged);
    };

    private _onVisibilityChanged = (value: IPickListSelection) => {
        const newVisibility = value.selectedItems[0].key as ProjectVisibilityType;
        this.visibilityChanged = newVisibility !== this.state.data.projectVisibility;
        this.currentVisibility = newVisibility;
        this._showSaveBtn(this.projectNameChanged || this.descriptionChanged || this.visibilityChanged);
    };

    private _showSaveBtn(enable: boolean): void {
        if (Boolean(this.projectNameValidationMessage.value)) {
            if (this.state.showSaveBtn) {
                this.setState({ showSaveBtn: false });
            }
        } else if (this.state.showSaveBtn !== enable) {
            this.setState({ showSaveBtn: enable });
        }
    }

    private _onSaveProjectDetailsClick = (): void => {
        if (this.projectNameChanged) {
            const newProjectName = this.projectNameTextField && this.projectNameTextField.value;
            if (!this.state.showRenameDialog) {
                if (newProjectName != null) {
                    this._startActionCapture("Open Project Rename Dialog", {});
                    this.setState({ showRenameDialog: true });
                    return;
                }
            }
        } else {
            this._saveProjectDetails();
        }
    };

    private _saveProjectDetails = (): void => {
        if (this.projectNameChanged) {
            this.setState({ showRenameDialog: false });
            this._startActionCapture("Update Project Name", {});
            const newProjectName = this.projectNameTextField && this.projectNameTextField.value;
            if (newProjectName != null) {
                this.updateNode.name = newProjectName;
            }
        } else if ("name" in this.updateNode) {
            delete this.updateNode.name;
        }

        if (this.descriptionChanged) {
            this._startActionCapture("Update Description", {});
            const newDescription = this.descriptionTextField && this.descriptionTextField.value;
            if (newDescription != null) {
                this.updateNode.description = newDescription;
            }
        } else if ("description" in this.updateNode) {
            delete this.updateNode.description;
        }

        if (this.visibilityChanged) {
            this._startActionCapture("Change Visibility", {});
            this.updateNode.visibility = projectVisibilityStringToEnum(this.currentVisibility);
        } else {
            this.updateNode.visibility = ProjectVisibility.Unchanged;
        }

        this.updateNode.state = ProjectState.Unchanged; // Project state is unchanged.

        this._updateProject(this.updateNode);
    };

    private _onImageFileSelected(error: string, imageData: ImageData): InputFileSelect {
        const result = {} as InputFileSelect;

        // File validation error from image select.
        if (error) {
            result.previewImage = false;
            result.error = error;
        } else {
            result.previewImage = true;
            result.error = "";
        }

        return result;
    }

    private _cancelImageUpload = (hasChanges: boolean) => {
        this.setState({ showChangeImageDialog: false });
    };

    private async _saveImageAction(resetImage: boolean, imageData: ImageData) {
        if (resetImage) {
            await this._removeImage();
        } else {
            await this._saveImage(imageData);
        }
    }

    private _saveImage(imageData: ImageData): Promise<void> {
        const avatarBlob: IProjectAvatar = { image: imageData.byteArray };
        this.imageUpdateInProgress.value = true;
        this.setState({ showChangeImageDialog: false });
        const actionTelemetryLogger = this._startActionCapture("Set Project Avatar", {});
        return new Promise(() => {
            const restClient = this.context.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
            restClient.setProjectAvatar(avatarBlob, this.state.data.editProjectOptions.projectId).then(
                async () => {
                    await this._monitorProgress(0);
                    this._updateProjectImageUrl();
                },
                reason => {
                    this.setState({ error: reason.message || String(reason) }, () => this._updateProjectImageUrl());
                    actionTelemetryLogger.captureError(reason.message || String(reason));
                }
            );
        });
    }

    private _removeImage(): Promise<void> {
        this.setState({ showChangeImageDialog: false });
        this.imageUpdateInProgress.value = true;
        const actionTelemetryLogger = this._startActionCapture("Remove Project Avatar", {});
        return new Promise(() => {
            return new Promise(() => {
                const restClient = this.context.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
                restClient.removeProjectAvatar(this.state.data.editProjectOptions.projectId).then(
                    async () => {
                        await this._monitorProgress(0);
                        this._updateProjectImageUrl();
                    },
                    reason => {
                        this.setState({ error: reason.message || String(reason) }, () => this._updateProjectImageUrl());
                        actionTelemetryLogger.captureError(reason.message || String(reason));
                    }
                );
            });
        });
    }

    private _onDeleteProjectClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        this.setState({ showDeleteProjectDialog: true });
    };

    private _onDeleteProject = () => {
        this.setState({ showDeleteProjectDialog: false });
        const actionTelemetryLogger = this._startActionCapture("Delete Project", {});
        const restClient = this.context.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        restClient.queueDeleteProject(this.state.data.deleteProjectOptions.projectId).then(
            async opref => {
                this.setState({ updateInProgress: true });
                this._operationId = opref.id;
                await this._monitorProgress(0);
                const url = routeUrl(this.context.pageContext, "ms.vss-tfs-web.suite-me-page-route");
                window.location.href = url;
            },
            reason => {
                this.setState({ updateInProgress: false, error: reason.message || String(reason) });
                actionTelemetryLogger.captureError(reason.message);
            }
        );
    };

    private _updateProject(project: Partial<TeamProject>): void {
        const actionTelemetryLogger = this._startActionCapture("Update Project", {});
        const restClient = this.context.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
        // we have to cast because updateProject() wants a TeamProject but it will actually take a Partial<TeamProject>
        restClient.updateProject(project as TeamProject, this.state.data.editProjectOptions.projectId).then(
            opref => {
                this.setState({ updateInProgress: true });
                this._operationId = opref.id;
                this._monitorProgress(0);
            },
            reason => {
                this.setState({ updateInProgress: false, error: reason.message || String(reason) });
                actionTelemetryLogger.captureError(reason.message);
            }
        );
    }

    /**
     * Poll operations client to see if operation has completed.
     */
    private _monitorProgress = async (updateCount: number): Promise<void> => {
        if (updateCheckCount > updateCount) {
            if (this._operationId) {
                const operationsClient = this.context.pageContext.getRestClient<IOperationsRestClient>("IOperationsRestClient");
                let completed = false;

                try {
                    const op = await operationsClient.getOperation(this._operationId);

                    switch (op.status) {
                        case OperationStatus.Succeeded:
                            completed = true;
                            break;
                        case OperationStatus.Cancelled:
                        case OperationStatus.Failed:
                            completed = true;
                            this.setState({ error: op.resultMessage });
                            break;
                    }
                } catch {
                    this.setState({
                        error: AdminResources.SaveError,
                        updateInProgress: false
                    });
                }

                if (completed) {
                    this._operationId = null;
                    this._refresh();
                } else {
                    await wait(progressPollInterval);
                    await this._monitorProgress(updateCount + 1);
                }
            }
        }
    };

    /**
     * Get updated data from the data provider.
     */
    private async _refresh(): Promise<void> {
        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");

        const pageData = pageService.getData()!;
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        try {
            const data = await contributionService.getDataAsync<IProjectOverviewData>(
                dataProviderId,
                {
                    pageSource: {
                        project: pageData.project
                    }
                },
                true // ignoreCache
            ); // We don't need to get the delay load data (project admins) here as they don't need to be refreshed.
            if (data) {
                if (this.state.data.displayName !== data.displayName) {
                    // the project has been renamed and we need to go to the updated URL
                    const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
                    const url = historyService.generateUrl({ project: data.displayName });
                    FastPageSwitch(this.context.pageContext, url, true);
                }
                this.updateNode = {};
                this.projectNameChanged = this.descriptionChanged = this.visibilityChanged = false;
                this.setState({ data: data, updateInProgress: false, showSaveBtn: false, error: undefined });
            } else {
                throw new Error("Data provider returned null");
            }
        } catch (ex) {
            traceException(this.context.pageContext, "admin", "projectoverview", ex);

            this.setState({
                error: AdminResources.WaitError,
                updateInProgress: false
            });
        }
    }

    private async _updateDelayLoad(projectId: string) {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const newdata = await contributionService.getDataAsync<IProjectOverviewDelayLoadData>(delayLoadDataProviderId, { projectId: projectId });

        if(newdata)
        {
            const uri = new Uri(this.projectImageUrl);
            uri.addQueryParam("generateDefaultMemberAvatar", "" + true, /*replaceExisting*/ true);
            newdata.defaultResetProjectImageUrl = uri.absoluteUri;
        }

        this.setState({ delayLoadData: newdata });
    }
    // private _getProjectId(): string {
    //     const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
    //     const pageData = pageService.getData();
    //     return pageData!.project!.id;
    // }
}
Components.add("projectOverview", ProjectOverviewComponent);
